/**
 * One row in the inbox list — counterparty avatar circle, nickname/address,
 * truncated last-message preview, amount, relative time, unread badge.
 *
 * Tap → navigate to /thread/:counterparty (display form, not raw — keeps
 * URLs human-readable).
 */
import { memo } from "react";
import { useLocation } from "wouter";
import type { InboxThread } from "../types/api";
import { formatTon, previewClip, relativeTime, shortenAddress } from "../lib/format";
import { haptics } from "../lib/telegram";

/** Color from a deterministic 5-color palette so similar wallets stay distinct. */
function avatarColor(seed: string): string {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0;
  const colors = [
    "bg-blue-500",
    "bg-emerald-500",
    "bg-amber-500",
    "bg-rose-500",
    "bg-violet-500",
  ];
  return colors[Math.abs(h) % colors.length] ?? "bg-blue-500";
}

interface Props {
  thread: InboxThread;
}

export const InboxRow = memo(function InboxRow({ thread }: Props): JSX.Element {
  const [, setLocation] = useLocation();

  const displayName = thread.nickname ?? shortenAddress(thread.counterparty);
  const initials = (thread.nickname ?? thread.counterparty.slice(2, 4))
    .slice(0, 2)
    .toUpperCase();
  const preview = previewClip(thread.last_message.comment, 80);
  const directionPrefix = thread.last_message.direction === "out" ? "You: " : "";

  function open(): void {
    haptics.selection();
    setLocation(`/thread/${encodeURIComponent(thread.counterparty)}`);
  }

  return (
    <button
      onClick={open}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
      className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors active:bg-tg-secondary-bg/70 focus:bg-tg-secondary-bg/70 focus:outline-none"
      role="listitem"
    >
      <div
        className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white ${avatarColor(
          thread.counterparty_raw,
        )}`}
        aria-hidden="true"
      >
        {initials}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate font-medium text-tg-text">
            {displayName}
            {thread.muted ? (
              <span className="ml-1 text-tg-hint" aria-label="muted">
                &middot; muted
              </span>
            ) : null}
          </span>
          <span className="shrink-0 text-xs text-tg-hint">
            {relativeTime(thread.last_message.chain_ts)}
          </span>
        </div>
        <div className="flex items-baseline justify-between gap-2">
          <span className="truncate text-sm text-tg-hint">
            {directionPrefix}
            {preview}
          </span>
          <span className="shrink-0 text-xs text-tg-hint">
            {formatTon(thread.last_message.amount_nano)}
          </span>
        </div>
      </div>
      {thread.unread_count > 0 ? (
        <div className="ml-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-tg-button px-1.5 text-[11px] font-semibold text-tg-button-text">
          {thread.unread_count > 99 ? "99+" : thread.unread_count}
        </div>
      ) : null}
    </button>
  );
});
